import Prismic from '@prismicio/client';
import { Document } from '@prismicio/client/types/documents';
import {
  GetStaticPaths,
  GetStaticProps,
  GetStaticPropsContext,
  NextPage,
} from 'next';
import Head from 'next/head';
import SliceZone from 'next-slicezone';
import resolver from 'sm-resolver';
import Layout from 'components/Layout';
import useUpdatePreviewRef from 'hooks/useUpdatePreviewRef';
import {
  getMetaAlternates,
  getMetaText,
  getRealLocale, getUidPath, getVerticalId,
} from 'utils/common';
import { ParsedUrlQuery } from 'querystring';
import { Ref } from '@prismicio/client/types/ResolvedApi';
import set from 'lodash.set';
import cloneDeep from 'lodash.clonedeep';
import { nanoid } from 'nanoid';
import {
  PRISMIC_API_ENDPOINT, SPANISH_LOCALE, PUBLIC_DEFAULT_LOCALE,
} from '../utils/constants';

interface Props {
  header: Document;
  page: Document;
  footer: Document;
  preview: { activeRef: Ref };
}

type Page = NextPage<Props> & { Layout?: React.FC };

const Page: Page = ({
  page, header, footer, preview,
}) => {
  useUpdatePreviewRef(preview, page.id);
  const metaTitle = getMetaText(page.data?.meta_title, page.lang);
  const metaDescription = getMetaText(page.data?.meta_description, page.lang);
  const metaKeywords = page.data?.meta_keywords?.map(({ word }) => word)?.filter(Boolean) ?? [];
  const metaAlternate = getMetaAlternates({ pages: [cloneDeep(page), ...page.alternate_languages] });

  return page?.data?.meta_redirect?.url ? (
    <Head>
      <meta
        httpEquiv="refresh"
        content={`0; url=${page.data.meta_redirect.url}`}
      />
    </Head>
  ) : (
    <Layout header={header} footer={footer} resolver={resolver}>
      <Head>
        <title>{metaTitle}</title>
        <meta property="og:title" content={metaTitle} key="title" />
        <meta name="description" content={metaDescription} />
        {page.data?.noindex && <meta name="robots" content="noindex" />}
        {!!metaKeywords.length && <meta name="keywords" content={metaKeywords.join(', ')} />}
        {metaKeywords.map((word: string) => <meta key={nanoid()} property="article:tag" content={word} />)}
        {metaAlternate.map(({ lang, href }) => (<link key={nanoid()} rel="alternate" hrefLang={lang} href={href} />))}
      </Head>
      <SliceZone resolver={resolver} slices={page.data?.slices} />
    </Layout>
  );
};

type TPreviewData = GetStaticPropsContext & { previewData? };

export const getStaticProps: GetStaticProps = async ({
  locale,
  params,
  previewData,
}: TPreviewData) => {
  const ref = previewData ? previewData.ref : null;

  const currentLocale = getRealLocale(
    locale || PUBLIC_DEFAULT_LOCALE || SPANISH_LOCALE,
  );

  const client = Prismic.client(PRISMIC_API_ENDPOINT);

  const headerRequest = client.getSingle('header', {
    lang: currentLocale,
    ref,
  });

  const footerRequest = client.getSingle('footer', {
    lang: currentLocale,
    ref,
  });

  const pageRequest = client.getByUID('page', params?.uid as string, {
    lang: currentLocale,
    ref,
  });

  const verticalsRequest = client.query(
    [Prismic.predicates.at('document.type', 'verticalpage')],
    {
      lang: currentLocale,
      ref,
    },
  );

  const clustersRequest = client.query(
    [Prismic.predicates.at('document.type', 'clusterpage')],
    {
      lang: currentLocale,
      ref,
      pageSize: 100,
    },
  );

  const [header, footer, page, verticals, clusters] = await Promise.all(
    [headerRequest, footerRequest, pageRequest, verticalsRequest, clustersRequest],
  );

  if (page?.data?.slices) {
    const products = (await client.query(
      [Prismic.predicates.at('document.type', 'products')],
      {
        lang: currentLocale,
        ref,
      },
    )).results.map((item) => ({
      ...item,
      data: {
        ...item.data,
        verticalId: getVerticalId(item.data.clusterPage.uid, clusters.results),
      },
    }));

    page.data.slices = page.data.slices.map((slice) => ({
      ...slice,
      _products: products,
      _clusters: clusters.results,
      _verticals: verticals.results,
      _page: {
        type: page.type,
      },
    }));
  }

  if (!header || !footer || !page) {
    return {
      notFound: true,
    };
  }

  const products = header.data.products.map(
    (item) => set(item, 'link.data.verticalId', getVerticalId(item.link.uid, clusters.results)),
  );

  return {
    props: {
      header: {
        ...header.data,
        products,
        clusters: clusters.results,
        verticals: verticals.results,
      },
      footer: footer.data,
      page,
      preview: {
        activeRef: ref,
      },
    },
  };
};

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  const client = Prismic.client(PRISMIC_API_ENDPOINT);

  const pages = await client.query(
    [Prismic.Predicates.at('document.type', 'page')],
    {
      lang: '*',
    },
  );

  const paths: {
    params: ParsedUrlQuery;
    locale?: string | undefined;
  }[] = [];

  locales?.forEach((locale) => {
    pages.results.forEach(({ uid, lang }) => {
      if (
        uid
        && uid !== getUidPath('courses', locale)
        && lang
        && getRealLocale(locale).toLowerCase() === lang.toLowerCase()
      ) {
        paths.push({
          params: { uid },
          locale,
        });
      }
    });
  });

  return {
    paths,
    fallback: 'blocking',
  };
};

export default Page;
